import {
  SpeechToTextProviderType,
  TranslationProviderType,
} from "@auvious/asr";
import {
  ApplicationProviderTypeEnum,
  AmazonAuthModeEnum,
  AIFeatureEnum,
} from "../../core-ui.enums";
import { IAppointmentNotification } from "../Appointment";
import { IClient } from "@auvious/client";

export interface McpConfiguration {
  type: "mcp";
  enabled: boolean;
  serverLabel: string;
  serverDescription?: string;
  serverUrl: string;
  allowedTools?: string[];
  headers?: Record<string, string>;
}

export interface IApplicationServiceParams extends ITranscriptServiceParams {
  storageProvider?: ApplicationProviderTypeEnum;
  storageProviderKey?: string;
  storageProviderBucket?: string;
  callbackParameters?: IApplicationServiceCallbackParams;
  storageProviderTested?: boolean;
  asr?: IASRServiceParams & IASRGoogleServiceParams;
  translate?: ITranslateServiceParams &
    ITranslateGoogleAdvancedServiceParams &
    ITranslateGoogleBasicServiceParams;
  appointmentParameters?: IAppointmentServiceParams;
  tokenSessionStoreEnabled?: boolean;
  digitalConnect?: IDigitalConnectServiceParams;
  estimatedWaitTimeParameters?: IClientCredentialsParams;
  interactionMonitorParameters?: IClientCredentialsParams;
  anonymousRegistration?: IAnonymouseRegistration;
  aiAssistantCompanyProfile?: string;
  aiAssistantSystemPrompt?: string;
  aiParameters?: IApplicationServiceAIParams;
  liveToolConfiguration?: string | null;
}

type AIProviderServiceParams = {
  [key in ApplicationProviderTypeEnum]?: {
    speech?: {
      languageCode?: string;
      voiceName?: string;
    };
    config?: IAIProviderConfig;
  };
};

export interface ProviderFeatureParams {
  provider?: ApplicationProviderTypeEnum;
}

export interface IApplicationServiceAIParams extends AIProviderServiceParams {
  mcp?: McpConfiguration[];
  authCredentials?: IClientCredentialsParams;
  features?: Record<AIFeatureEnum, ProviderFeatureParams>;
  // { transcription?: ProviderFeatureParams;
  // analysis?: ProviderFeatureParams;
  // translation?: ProviderFeatureParams;
  // vision?: ProviderFeatureParams;
  // realTimeAudioVideo?: ProviderFeatureParams;
  // chat?: ProviderFeatureParams;
  // };
}

export interface ITranscriptServiceParams
  extends ITranscriptServiceBaseParams,
    ITranscriptGoogleServiceParams,
    ITranscriptOpenAIServiceParams {}

export interface ITranscriptServiceBaseParams {
  transcriptProvider?: ApplicationProviderTypeEnum;
  // @deprecated, region is saved in each provider config
  aiProviderDefaultRegion?: string;

  // google transcript specific storage provider config
  speechToTextStorageProvider?: ApplicationProviderTypeEnum;
  speechToTextStorageProviderBucket?: string;
  speechToTextStorageProviderKey?: string;
  // ---|
}

export interface ITranscriptGoogleServiceParams {
  googleAsrOfflineConfiguration?: string;
  googleSpeechRecognizer?: string;
}

export interface ITranscriptOpenAIServiceParams {
  openAIConfiguration?: string;
}

export interface IAIProviderConfig {
  credentials?: string;
  region?: string;
  // backwards compatibility --
  storageProviderBucket?: string;
  storageProviderKey?: string;
}

export interface IAIProviderGoogleConfig extends IAIProviderConfig {
  speechRecognizer?: string;
}

// export interface

export const DEFAULT_TRANSCRIPT_PARAMS: ITranscriptServiceParams = {
  speechToTextStorageProvider: null,
  speechToTextStorageProviderBucket: null,
  speechToTextStorageProviderKey: null,
  googleAsrOfflineConfiguration: null,
  googleSpeechRecognizer: null,
  openAIConfiguration: null,
  transcriptProvider: null,
  // aiProviderDefaultRegion: null,
};

export const DEFAULT_AI_PROVIDER_CONFIG: IAIProviderConfig = {
  credentials: null,
};

export interface IClientCredentialsParams {
  clientId: string;
  clientSecret: string;
}

export interface IDigitalConnectServiceParams {
  clientCredentials?: IClientCredentialsParams;
}

export interface IAppointmentServiceParams {
  deploymentId?: string; // genesys
  touchpointId?: string; // talkdesk
  base_url?: string;
  // to be deprecated
  clientId?: string;
  clientSecret?: string;
  // ----
  smsClientId?: string;
  smsClientSecret?: string;
  smsNumber?: string;
  notifications?: IAppointmentNotification[];
  notificationsEnabled?: boolean;
  notificationChannelSMSEnabled?: boolean;
  notificationChannelEmailEnabled?: boolean;
  notificationChannelWebhooksEnabled?: boolean;
  singleUseAppointmentTicketEnabled?: boolean;
  ticketLength?: number;
  emailClientId?: string;
  emailClientSecret?: string;
  emailDomain?: string;
  emailFromName?: string;
  autoActivate?: boolean;
  webhooksSubscriptionId?: string;
  filterByAssignedAgent?: boolean;
  agentReassignmentEnabled?: boolean;
  ticketTTL?: number; // seconds. defaults to 1h
  urlLocaleEnabled?: boolean; // include the appointment locale (?lang=) in customer join/manage links
}

export interface IApplicationServiceCallbackParams {
  clientId: string;
  clientSecret: string;
}

export interface IGoogleCloudApplicationServiceParams
  extends IApplicationServiceParams {
  storageProviderKey: string;
}

export interface IAmazonS3ApplicationServiceParams
  extends IApplicationServiceParams {
  storageProviderAccessKeyId: string;
  storageProviderSecretAccessKey: string;
  storageProviderRegion: string;
  storageProviderAuthMode: AmazonAuthModeEnum;
  storageProviderRoleArn?: string;
  storageProviderRoleSessionName?: string;
  storageProviderExternalId?: string;
  storageProviderSessionDurationSeconds?: number;
  storageProviderAwsDisableAuthRetry?: boolean;
}

export interface IMicrosoftAzureApplicationServiceParams
  extends IApplicationServiceParams {
  storageProviderConnectionString: string;
}

export interface ISFTPApplicationServiceParams
  extends IApplicationServiceParams {
  storageProviderHost: string;
  storageProviderPort: string;
  storageProviderUser: string;
  storageProviderKeyPairId: string;
  storageProviderKeyPairSource: "new" | "existing";
}

export const DEFAULT_STORAGE_GOOGLE_CLOUD_PARAMS: IGoogleCloudApplicationServiceParams =
  {
    storageProvider: ApplicationProviderTypeEnum.GOOGLE_CLOUD,
    storageProviderBucket: null,
    storageProviderKey: null,
    storageProviderTested: false,
  };

export const DEFAULT_STORAGE_AMAZON_S3_PARAMS: IAmazonS3ApplicationServiceParams =
  {
    storageProvider: ApplicationProviderTypeEnum.AMAZON_S3,
    storageProviderBucket: null,
    storageProviderAccessKeyId: null,
    storageProviderSecretAccessKey: null,
    storageProviderRegion: null,
    storageProviderTested: false,
    storageProviderAuthMode: AmazonAuthModeEnum.staticCredentials,
  };

export const DEFAULT_STORAGE_MICROSOFT_AZURE_PARAMS: IMicrosoftAzureApplicationServiceParams =
  {
    storageProvider: ApplicationProviderTypeEnum.MICROSOFT_AZURE,
    storageProviderBucket: null,
    storageProviderConnectionString: null,
    storageProviderTested: false,
  };

export const DEFAULT_STORAGE_SFTP_PARAMS: ISFTPApplicationServiceParams = {
  storageProvider: ApplicationProviderTypeEnum.SFTP,
  storageProviderBucket: null, // base directory
  storageProviderHost: null,
  storageProviderPort: "22",
  storageProviderUser: null,
  storageProviderKeyPairId: null,
  storageProviderTested: false,
  storageProviderKeyPairSource: "new",
};

// export interface

export interface IASRServiceParams {
  providers?: SpeechToTextProviderType[];
}

export interface ITranslateServiceParams {
  providers?: TranslationProviderType[];
}

export interface ITranslateGoogleBasicServiceParams
  extends ITranslateServiceParams {
  GOOGLE_BASIC?: string;
}

export const DEFAULT_TRANSLATE_GOOGLE_BASIC_PARAMS: ITranslateGoogleBasicServiceParams =
  {
    GOOGLE_BASIC: null,
  };

export interface ITranslateGoogleAdvancedServiceParams
  extends ITranslateServiceParams {
  GOOGLE_ADVANCED?: string;
  googleTranslateAdvancedLocation?: string;
}

export const DEFAULT_TRANSLATE_GOOGLE_ADVANCED_PARAMS: ITranslateGoogleAdvancedServiceParams =
  {
    GOOGLE_ADVANCED: null,
    googleTranslateAdvancedLocation: null,
  };

export interface IASRGoogleServiceParams extends IASRServiceParams {
  GOOGLE?: string;
}

export const DEFAULT_ASR_GOOGLE_PARAMS: IASRGoogleServiceParams = {
  GOOGLE: null,
};

export type AnonymousPermission =
  | "REGISTER"
  | "ICE_SERVERS_READ"
  | "CONFERENCE_CREATE"
  | "CONFERENCE_JOIN"
  | "SEND_EVENT";

export const ANONYMOUS_PERMISSIONS: AnonymousPermission[] = [
  "REGISTER",
  "ICE_SERVERS_READ",
  "CONFERENCE_CREATE",
  "CONFERENCE_JOIN",
  "SEND_EVENT",
];

export type AnonymousRole = "CUSTOMER";

export const ANONYMOUS_ROLES: AnonymousRole[] = ["CUSTOMER"];

export interface IAnonymousCaptchaConfiguration {
  provider: string;
  siteKey: string;
  secret: string;
  version?: "v2" | "v3";
  expectedScore?: number;
  expected_score?: number;
  enterprise?: boolean;
  projectId?: string;
  project_id?: string;
  apiKey?: string;
  api_key?: string;
}

export interface IAnonymousRegistrationRequiredToken {
  header?: string;
  parameter?: string;
  expected?: string;
}

export interface IAnonymousRegistrationCommon {
  allowedOrigins?: string[];
  additionalRoles?: AnonymousRole[];
  additionalPermissions?: AnonymousPermission[];
  captcha: IAnonymousCaptchaConfiguration;
  requiredTokens?: IAnonymousRegistrationRequiredToken[];
}

export interface IAnonymousRegistrationVariant
  extends IAnonymousRegistrationCommon {}

export interface IAnonymouseRegistration extends IAnonymousRegistrationCommon {
  enabled: boolean;
  defaultVariant?: string;
  variants?: Record<string, IAnonymousRegistrationVariant>;
}

// export enum ServiceParam {
//   STORAGE_PROVIDER = "storageProvider",
//   STORAGE_PROVIDER_BUCKET = "storageProviderBucket",
// }

/** only role: ADMIN has access */
export class ServiceOptions implements IApplicationServiceParams {
  public static OAUTH_CLIENT_ID = "clientId";
  public static OAUTH_CLIENT_SECRET = "clientSecret";
  public static STORAGE_PROVIDER = "storageProvider";
  public static STORAGE_PROVIDER_BUCKET = "storageProviderBucket";
  public static ASR = "asr";
  public static TRANSLATE = "translate";
  public static APPOINTMENT = "appointmentParameters";
  public static DIGITAL_CONNECT = "digitalConnect";
  public static QUEUE_ESTIMATED_WAIT_TIME_PARAMS =
    "estimatedWaitTimeParameters";
  public static WIDGET_HEARTBEAT_PARAMS = "interactionMonitorParameters";
  public static SPEECH_TO_TEXT_STORAGE_PROVIDER = "speechToTextStorageProvider";
  public static SPEECH_TO_TEXT_STORAGE_PROVIDER_BUCKET =
    "speechToTextStorageProviderBucket";
  public static SPEECH_TO_TEXT_STORAGE_PROVIDER_KEY =
    "speechToTextStorageProviderKey";
  public static GOOGLE_ASR_OFFLINE_CONFIGURATION =
    "googleAsrOfflineConfiguration";
  public static ANONYMOUS_REGISTRATION = "anonymousRegistration";
  public static AI_ASSISTANT_COMPANY_PROFILE = "aiAssistantCompanyProfile";
  public static AI_ASSISTANT_SYSTEM_PROMPT = "aiAssistantSystemPrompt";

  private _options: IApplicationServiceParams;

  constructor(options: IApplicationServiceParams) {
    this._options = options;
  }

  public get values(): IApplicationServiceParams {
    return {
      storageProvider: null,
      storageProviderBucket: null,
      storageProviderTested: false,
      // aiProviderDefaultRegion: null,
      speechToTextStorageProvider: null,
      speechToTextStorageProviderBucket: null,
      speechToTextStorageProviderKey: null,
      googleAsrOfflineConfiguration: null,
      aiAssistantCompanyProfile: null,
      aiAssistantSystemPrompt: null,
      liveToolConfiguration: null,
      ...this._options,
      aiParameters: {
        authCredentials: null,
        ...this._options?.aiParameters,
        features: {
          transcription: {
            provider: null,
            ...this._options?.aiParameters?.features?.transcription,
          },
          analysis: {
            provider: null,
            ...this._options?.aiParameters?.features?.analysis,
          },
          translation: {
            provider: null,
            ...this._options?.aiParameters?.features?.translation,
          },
          vision: {
            provider: null,
            ...this._options?.aiParameters?.features?.vision,
          },
          realTimeAudioVideo: {
            provider: null,
            ...this._options?.aiParameters?.features?.realTimeAudioVideo,
          },
          chat: {
            provider: null,
            ...this._options?.aiParameters?.features?.chat,
          },
          ...this._options?.aiParameters?.features,
        },
      },
      callbackParameters: {
        clientId: null,
        clientSecret: null,
        ...this._options?.callbackParameters,
      },
      asr: {
        providers: [],
        ...this._options?.asr,
      },
      translate: {
        providers: [],
        ...this._options?.translate,
      },
      appointmentParameters: {
        base_url: "",
        deploymentId: null,
        clientId: null,
        clientSecret: null,
        notificationsEnabled: true,
        notificationChannelSMSEnabled: false,
        notificationChannelEmailEnabled: false,
        notificationChannelWebhooksEnabled: false,
        singleUseAppointmentTicketEnabled: false,
        ticketLength: 6,
        notifications: [],
        autoActivate: true,
        webhooksSubscriptionId: null,
        filterByAssignedAgent: true,
        agentReassignmentEnabled: false,
        ticketTTL: 3600, // seconds, 1 hour
        urlLocaleEnabled: false,
        ...this._options?.appointmentParameters,
      },
      digitalConnect: {
        clientCredentials: {
          clientId: null,
          clientSecret: null,
          ...this._options?.digitalConnect?.clientCredentials,
        },
        ...this._options?.digitalConnect,
      },
      estimatedWaitTimeParameters: {
        clientId: null,
        clientSecret: null,
        ...this._options?.estimatedWaitTimeParameters,
      },
      interactionMonitorParameters: {
        clientId: null,
        clientSecret: null,
        ...this._options?.interactionMonitorParameters,
      },
      anonymousRegistration: {
        enabled: false,
        ...this._options?.anonymousRegistration,
      },
      // all first-level props should go at the top, before ...this._options
    };
  }
  public get oAuthClientId() {
    return this._options?.callbackParameters?.clientId;
  }
  public get oAuthClientSecret() {
    return this._options?.callbackParameters?.clientSecret;
  }
  public get storageProvider() {
    return this._options?.storageProvider;
  }
  public get storageProviderBucket() {
    return this._options?.storageProviderBucket;
  }
  public get asrProvider() {
    return this._options?.asr || { providers: [] };
  }
  public get translateProvider() {
    return this._options?.translate || { providers: [] };
  }
  public get appointmentParameters() {
    return this._options?.appointmentParameters;
  }
  public get appointmentDeploymentId() {
    return this._options?.appointmentParameters?.deploymentId;
  }
  public get appointmentBaseUrl() {
    return this._options?.appointmentParameters?.base_url;
  }
  public get anonymousRegistration() {
    return this._options?.anonymousRegistration;
  }
  public get aiAssistantCompanyProfile() {
    return this._options?.aiAssistantCompanyProfile;
  }
}
